import {
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Toolbar,
  useMediaQuery,
  Collapse,
  ListItemIcon,
} from "@mui/material";
import { ExpandLess, ExpandMore, ChevronRight } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { TOP_BAR_HEIGHT, DRAWER_WIDTH } from "../constants/appConstants";
import { getPhotoMenuRequest } from "../api/photo-api"


const drawerWidth = DRAWER_WIDTH;

interface MenuNode {
  labelKey: string;
  path?: string;
  onClick?: () => void;
  children?: MenuNode[];
}

interface Props {
  mobileOpen: boolean;
  handleDrawerToggle: () => void;
}

const ResponsiveDrawer: React.FC<Props> = ({ mobileOpen, handleDrawerToggle }) => {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { t } = useTranslation();

  const [openNodes, setOpenNodes] = useState<Record<string, boolean>>({});
  const [photoChildren, setPhotoChildren] = useState<MenuNode[] | null>(null);
  const [loadingPhotos, setLoadingPhotos] = useState(false);

  // Load XML for Photos menu lazily
  const loadPhotoChildren = async () => {
    setLoadingPhotos(true);
    try {
      const response = await getPhotoMenuRequest();
      
      // Map PhotoMenu -> MenuNode[]
      const nodes: MenuNode[] = response.year.map((y) => ({
        labelKey: y.name,
        children: y.set.map((s) => ({
          labelKey: s.titleKey || s.title || "",
          path: `/photos/${y.name}/${s.name}`,
        })),
      }));
      
      setPhotoChildren(nodes);
    } catch (err) {
      // Global Snackbar handles error
      setLoadingPhotos(false);
    } finally {
      setLoadingPhotos(false);
    }
  };

  const toggleNode = (node: MenuNode) => {
    setOpenNodes((prev) => ({ ...prev, [node.labelKey]: !prev[node.labelKey] }));

    // Lazy load Photos children
    if (node.labelKey === "menu.photos" && !photoChildren) {
      loadPhotoChildren();
    }
  };

  // Build menu tree dynamically on every render
  const menuTree: MenuNode[] = [
    { labelKey: "menu.introduction", path: "/" },
    {
      labelKey: "menu.actions",
      children: [
        { labelKey: "menu.plannedActions", path: "/actions/list/planned" },
        { labelKey: "menu.pastActions", path: "/actions/list/recent" },
      ],
    },
    {
      labelKey: "menu.photos",
      children: photoChildren ?? [], // empty array initially
    },
    { labelKey: "menu.contacts", path: "/contacts" },
  ];

  // Recursive render of nodes
  const renderNodes = (nodes: MenuNode[], level = 0) => (
    <List disablePadding sx={{ pl: level * 2 }}>
      {nodes.map((node) => {
        const hasChildren = !!node.children?.length || node.labelKey === 'menu.photos';
        const isOpen = openNodes[node.labelKey] || false;

        const handleClick = () => {
          if (node.labelKey === "menu.photos" || hasChildren) {
            toggleNode(node);
          } else if (node.path) {
            navigate(node.path);
            // 👇 close drawer on mobile
            if (!isDesktop) {
              handleDrawerToggle();
            }
          } else if (node.onClick) {
            node.onClick();
            // 👇 close drawer on mobile
            if (!isDesktop) {
              handleDrawerToggle();
            }
          }
        };

        return (
          <div key={node.labelKey}>
            <ListItemButton onClick={handleClick} sx={{ gap: "5px" }}>
              {level > 0 && (
                <ListItemIcon sx={{ minWidth: "auto" }}>
                  <ChevronRight fontSize="small" />
                </ListItemIcon>
              )}
              <ListItemText
                primary={
                  // Use translation unless label looks like a year number
                  node.labelKey.match(/^\d{4}$/)
                    ? node.labelKey
                    : t(node.labelKey) +
                      (node.labelKey === "menu.photos" && loadingPhotos ? " (loading...)" : "")
                }
              />
              {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
            </ListItemButton>
            {hasChildren && (
              <Collapse in={isOpen} timeout="auto" unmountOnExit>
                {renderNodes(node.children!, level + 1)}
              </Collapse>
            )}
          </div>
        );
      })}
    </List>
  );

  const drawerContent = (
    <div>
      <Toolbar sx={{ height: TOP_BAR_HEIGHT }} />
      <List>
        <ListItemButton
          onClick={() => {
            logout();
            navigate("/login");
          }}
        >
          <ListItemText primary={t("menu.logout")} />
        </ListItemButton>

        <Divider />

        {renderNodes(menuTree)}
      </List>
    </div>
  );

  return (
    <Drawer
      variant={isDesktop ? "permanent" : "temporary"}
      open={isDesktop ? true : mobileOpen}
      onClose={handleDrawerToggle}
      sx={{
        width: drawerWidth,
        "& .MuiDrawer-paper": { width: drawerWidth },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default ResponsiveDrawer;